import { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { CHAPTERS } from "./story";

const EASE = [0.22, 1, 0.36, 1];

const ChapterBlock = ({ chapter, registerRef }) => {
  return (
    <div
      ref={registerRef}
      data-chapter-index={chapter.index}
      className="flex min-h-screen flex-col justify-center py-16"
    >
      <motion.div
        initial={{ opacity: 0, y: 60 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{
          duration: 0.9,
          ease: EASE,
        }}
        className="group relative overflow-hidden rounded-[28px]
        border border-white/10
        bg-[#0F172A]
        backdrop-blur-xl"
      >
        {/* IMAGE */}

        <div className="relative h-[260px] overflow-hidden sm:h-[340px]">
          <img
            src={chapter.image}
            alt={chapter.title}
            className="h-full w-full object-cover opacity-70
            transition-transform
            duration-[1200ms]
            ease-out
            group-hover:scale-105"
          />

          {/* Overlay */}

          <div className="absolute inset-0 bg-gradient-to-t
          from-[#0F172A]
          via-[#0F172A]/30
          to-transparent" />

          {/* Tag */}

          <div
            className="
            absolute
            left-5
            top-5
            inline-flex
            items-center
            gap-2
            rounded-full
            border
            border-white/15
            bg-black/40
            px-3
            py-1.5
            backdrop-blur-md
          "
          >
            <span className="pulse-dot h-2 w-2 rounded-full bg-[#21A6E9]" />

            <span
              className="
              text-[10px]
              tracking-[0.25em]
              text-[#21A6E9]
            "
            >
              {chapter.tag}
            </span>
          </div>

          {/* Huge Watermark Number */}

          <span
            className="
            pointer-events-none
            absolute
            bottom-3
            right-5
            font-black
            text-[6rem]
            leading-none
            text-white/10
          "
          >
            {chapter.index}
          </span>
        </div>

        {/* CONTENT */}

        <div className="p-7 sm:p-9">

          <h3
            className="
            text-2xl
            font-bold
            uppercase
            tracking-tight
            text-white
            sm:text-3xl
          "
          >
            {chapter.title}
          </h3>

          <p
            className="
            mt-4
            max-w-lg
            text-sm
            leading-relaxed
            text-slate-400
          "
          >
            {chapter.description}
          </p>

        </div>

      </motion.div>
    </div>
  );
};

export default function StoryChapters() {
  const [active, setActive] = useState(CHAPTERS[0].index);
  const refs = useRef([]);
  // Tracks each card's intersection ratio with the center band so we can
  // pick a winner even when several cards are partially in that band at once.
  const ratiosRef = useRef({});

  const sidebarRefs = useRef([]);
  const [rail, setRail] = useState({ top: 0, height: 0 });

  useEffect(() => {
    const measure = () => {
      const activeIndex = CHAPTERS.findIndex((c) => c.index === active);
      const el = sidebarRefs.current[activeIndex];
      if (el) {
        setRail({ top: el.offsetTop, height: el.offsetHeight });
      }
    };

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [active]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = entry.target.dataset.chapterIndex;
          ratiosRef.current[index] = entry.intersectionRatio;
        });

        let winnerIndex = null;
        let winnerRatio = 0;

        Object.entries(ratiosRef.current).forEach(([index, ratio]) => {
          if (ratio > winnerRatio) {
            winnerRatio = ratio;
            winnerIndex = index;
          }
        });

        if (winnerIndex !== null) {
          setActive(winnerIndex);
        }
      },
      {
        rootMargin: "-45% 0px -45% 0px",
        threshold: Array.from({ length: 101 }, (_, i) => i / 100),
      }
    );

    refs.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="how-it-works"
      className="relative bg-[#020617] py-28"
    >
      <div className="section-container">

        {/* Header */}

        <div className="mb-8 flex items-center gap-3">
          <span className="h-2 w-2 rounded-full bg-[#21A6E9]" />

          <span
            className="
            text-xs
            uppercase
            tracking-[0.35em]
            text-slate-400
          "
          >
            THE CLAIM JOURNEY
          </span>
        </div>

        <h2
          className="
          mb-16
          max-w-4xl
          text-4xl
          font-bold
          uppercase
          leading-[0.95]
          tracking-tight
          text-white
          md:text-6xl
        "
        >
          SEVEN STEPS FROM

          <span className="text-[#21A6E9]">
            {" "}
            BREAKDOWN
          </span>

          <br />

          TO

          <span className="text-[#21A6E9]">
            {" "}
            BACK ON THE ROAD
          </span>

        </h2>

        {/* Main Layout */}

        <div className="grid gap-14 lg:grid-cols-[360px_minmax(0,1fr)]">

          {/* Sticky Sidebar */}

          <div className="hidden lg:block">

            <div className="sticky space-y-1" style={{
    top: "110px",
    alignSelf: "start"
}}>

              <div className="relative">

                {/* Background rail — static, spans full height of all items */}

                <div
                  className="absolute left-0 top-0 w-[2px]"
                  style={{
                    height: "100%",
                    background: "rgba(255,255,255,.08)",
                  }}
                />

                {/* Sliding highlight — colored segment matching the active row */}

                <div
                  className="absolute left-0 w-[2px] transition-all duration-500"
                  style={{
                    top: rail.top,
                    height: rail.height,
                    background: "#21A6E9",
                  }}
                />

                {CHAPTERS.map((chapter, i) => {

                  const isActive = active === chapter.index;

                  return (
                    <div
                      key={chapter.id}
                      ref={(el) => (sidebarRefs.current[i] = el)}
                      className="
                      relative
                      flex
                      items-center
                      gap-4
                      py-3
                      pl-5
                    "
                    >
                      {/* Number */}

                      <span
                        className="text-xs font-medium transition-all duration-500"
                        style={{
                          color: isActive
                            ? "#21A6E9"
                            : "#64748B",
                        }}
                      >
                        {chapter.index}
                      </span>

                      {/* Title */}

                      <span
                        className="
                        text-lg
                        font-bold
                        uppercase
                        tracking-tight
                        transition-all
                        duration-500
                      "
                        style={{
                          color: isActive
                            ? "#FFFFFF"
                            : "#64748B",

                          transform: isActive
                            ? "translateX(6px)"
                            : "translateX(0px)",
                        }}
                      >
                        {chapter.title}
                      </span>

                    </div>
                  );
                })}

              </div>

            </div>

          </div>

          {/* Right Content */}

          <div className="relative">

            {CHAPTERS.map((chapter, i) => (
              <ChapterBlock
                key={chapter.id}
                chapter={chapter}
                registerRef={(el) => (refs.current[i] = el)}
              />
            ))}

          </div>

        </div>

      </div>
    </section>
  );
}